import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navigation from './components/Navigation';
import Footer from './components/Footer';

// Pages
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import SetPassword from './pages/SetPassword';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import ClientDetails from './pages/ClientDetails';
import CheckIns from './pages/CheckIns';
import CheckInDetail from './pages/CheckInDetail';
import ClientCheckIns from './pages/ClientCheckIns';
import Materials from './pages/Materials';
import Analytics from './pages/Analytics';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import About from './pages/About';
import CheckInForm from './pages/CheckInForm';
import CheckInQuestions from './pages/CheckInQuestions';
import Progress from './pages/Progress';
import SubscriptionPackages from './pages/SubscriptionPackages';
import GroupTrainingPlans from './pages/GroupTrainingPlans';
import GroupZoomSessions from './pages/GroupZoomSessions';
import CheckoutSuccess from './pages/CheckoutSuccess';
import CheckoutCancel from './pages/CheckoutCancel';
import GroupOnboarding from './pages/GroupOnboarding';
import WorkoutPlanReview from './pages/WorkoutPlanReview';

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, roles }) => {
  console.log('ProtectedRoute rendered');
  const { isAuthenticated, user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: window.location.pathname }} replace />;
  }

  if (user?.needs_password_setup && location.pathname !== '/set-password') {
    const email = encodeURIComponent(user.email || '');
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/set-password?email=${email}&next=${next}`} replace />;
  }

  if (roles && user && !roles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Layout Component
const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  console.log('Layout rendered');
  return (
    <div className="min-h-screen">
      <Navigation />
      <main className="py-6">
        {children}
      </main>
      <Footer />
    </div>
  );
};

// App Routes Component
const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Home />} />
      <Route path="/about" element={<About />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/checkout/success" element={<CheckoutSuccess />} />
      <Route path="/checkout/cancel" element={<CheckoutCancel />} />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/onboarding-group"
        element={
          <ProtectedRoute roles={['client']}>
            <Layout>
              <GroupOnboarding />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Trainer Only Routes */}
      <Route
        path="/clients"
        element={
          <ProtectedRoute roles={['trainer']}>
            <Layout>
              <Clients />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/clients/:id"
        element={
          <ProtectedRoute roles={['trainer']}>
            <Layout>
              <ClientDetails />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/workout-plan-review"
        element={
          <ProtectedRoute roles={['trainer']}>
            <Layout>
              <WorkoutPlanReview />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/checkins"
        element={
          <ProtectedRoute>
            <Layout>
              <CheckIns />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/checkins/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <CheckInDetail />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/checkins/client/:clientId"
        element={
          <ProtectedRoute roles={['trainer']}>
            <Layout>
              <ClientCheckIns />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/checkins/new"
        element={
          <ProtectedRoute roles={['client']}>
            <Layout>
              <CheckInForm />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/checkins/:id/edit"
        element={
          <ProtectedRoute roles={['client']}>
            <Layout>
              <CheckInForm />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/checkins/questions"
        element={
          <ProtectedRoute roles={['trainer']}>
            <Layout>
              <CheckInQuestions />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/materials"
        element={
          <ProtectedRoute>
            <Layout>
              <Materials />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/analytics"
        element={
          <ProtectedRoute roles={['trainer']}>
            <Layout>
              <Analytics />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/subscription-packages"
        element={
          <ProtectedRoute roles={['trainer']}>
            <Layout>
              <SubscriptionPackages />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Client Only Routes */}
      <Route
        path="/checkin"
        element={
          <ProtectedRoute roles={['client']}>
            <Layout>
              <CheckInForm />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/progress"
        element={
          <ProtectedRoute roles={['client']}>
            <Layout>
              <Progress />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Group Client Routes */}
      <Route
        path="/group-training-plans"
        element={
          <ProtectedRoute roles={['client']}>
            <Layout>
              <GroupTrainingPlans />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/group-zoom-sessions"
        element={
          <ProtectedRoute roles={['client', 'trainer']}>
            <Layout>
              <GroupZoomSessions />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Common Protected Routes */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout>
              <Profile />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <Settings />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Navigation error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Something went wrong</h1>
            <p className="text-gray-600 mb-4">Please refresh the page or try again.</p>
            <button
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Main App Component
const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <ErrorBoundary>
          <AppRoutes />
        </ErrorBoundary>
      </AuthProvider>
    </Router>
  );
};

export default App;
